import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import DownloadIcon from '../../assets/download.png';
import flightIcon from '../../assets/flight-icon.svg';
import hotelIcon from '../../assets/hotel-reservation-icon.svg';
import visaIcon from '../../assets/visa-icon.svg';
import SubpageTitle from '../../components/SubpageTitle';
import { getCurrentAttendee } from '../../helpers/getters';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import { parseFieldsByInternalId } from '../../helpers/customFields';
import TabletExitButton from '../../components/TabletExitButton';
import { visaInvitationLinkEnabled, downloadVisaInvitationLink } from '../../helpers/visaInvitation';
import { getCompatibleDateFormat } from '../../helpers/dates';
import { INTERNAL_IDS } from '../../helpers/constants';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 785,
    maxWidth: '100%',
  },
  buttonsWrapper: {
    marginTop: theme.spacing(10),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60vh',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(4),
    },
  },
  icon: {
    margin: theme.spacing(4, 0),
  },
  downloadVisaInvitationButton: {
    textTransform: 'uppercase',
    fontWeight: 600,
    width: 432,
    maxWidth: '100%',
    padding: '0px 16px',
    minHeight: 39,
    color: '#ffffff',
    backgroundColor: theme.palette.primary.main,
    fontSize: 18,
    fontStyle: 'normal',
    fontFamily: 'Brilliant Cut Pro',
    lineHeight: '100%',
    letterSpacing: 0,
    textAlign: 'center',
    borderRadius: 0,
    border: 0,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& img': {
      width: 32,
      height: 32,
      objectFit: 'cover',
      marginRight: theme.spacing(),
    },
  },
  downloadButtonDisabled: {
    backgroundColor: theme.palette.lightGrey.dark,
    pointerEvents: 'none',
  },
  disabledDownloadMessage: {
    marginTop: theme.spacing(2),
    fontSize: 12,
  },
}));

const UnavailableDocument = ({ invitationLetter }) => {
  const classes = useStyles();
  return (
    <span className={classes.disabledDownloadMessage}>
      {invitationLetter
        ? 'Complete your profile information to download your invitation.'
        : 'This document is not available yet. Please check again soon.'}
    </span>
  );
};

UnavailableDocument.propTypes = {
  invitationLetter: PropTypes.bool,
};

UnavailableDocument.defaultProps = {
  invitationLetter: false,
};

const EventWallet = ({ entities, attendeeTypeCustomFields }) => {
  const classes = useStyles();
  const currentAttendeeFields = getCurrentAttendee(entities)?.fields;
  const fieldsId = parseFieldsByInternalId(currentAttendeeFields, attendeeTypeCustomFields);

  // TODO ajustar internal ids si se habilita wallet
  const downloadYourFlightTicket = currentAttendeeFields[fieldsId[INTERNAL_IDS.flightTicket]];
  const downloadYourHotelReservation = currentAttendeeFields[
    fieldsId[INTERNAL_IDS.hotelReservation]
  ];

  const visaFields = {
    firstName: currentAttendeeFields[fieldsId[INTERNAL_IDS.firstName]],
    lastName: currentAttendeeFields[fieldsId[INTERNAL_IDS.lastName]],
    birthdate: currentAttendeeFields[fieldsId[INTERNAL_IDS.birthdate]],
    jobTitle: currentAttendeeFields[fieldsId[INTERNAL_IDS.jobTitle]],
    // company: currentAttendeeFields[fieldsId[133]],
    passportNumber: currentAttendeeFields[fieldsId[INTERNAL_IDS.passportNumber]],
    passportExpiryDate: getCompatibleDateFormat(
      currentAttendeeFields[fieldsId[INTERNAL_IDS.passportExpiryDate]]
    ),
    checkinDate: getCompatibleDateFormat(currentAttendeeFields[fieldsId[INTERNAL_IDS.checkIn]]),
    checkoutDate: getCompatibleDateFormat(currentAttendeeFields[fieldsId[INTERNAL_IDS.checkOut]]),
    hotel: currentAttendeeFields[fieldsId[INTERNAL_IDS.hotelName]],
    zoneDetails: currentAttendeeFields[fieldsId[INTERNAL_IDS.zonesDetails]],
  };
  const visaButtonEnabled = visaInvitationLinkEnabled(visaFields);

  return (
    <div className={classes.root}>
      <TabletExitButton />
      <SubpageTitle text="Travel documents" />
      <div className={classes.buttonsWrapper}>
        <img className={classes.icon} src={flightIcon} alt="" />
        <a
          href={downloadYourFlightTicket?.originalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={clsx(
            classes.downloadVisaInvitationButton,
            !downloadYourFlightTicket && classes.downloadButtonDisabled
          )}
        >
          <img
            src={DownloadIcon}
            alt=""
          />
          Flight Ticket
        </a>
        {!downloadYourFlightTicket && <UnavailableDocument /> }
        <img className={classes.icon} src={hotelIcon} alt="" />
        <a
          href={downloadYourHotelReservation?.originalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={clsx(
            classes.downloadVisaInvitationButton,
            !downloadYourHotelReservation && classes.downloadButtonDisabled
          )}
        >
          <img
            src={DownloadIcon}
            alt=""
          />
          Hotel Reservation
        </a>
        {!downloadYourHotelReservation && <UnavailableDocument /> }
        <img className={classes.icon} src={visaIcon} alt="" />
        <a
          href={
            visaButtonEnabled
              ? downloadVisaInvitationLink(visaFields) : undefined
          }
          target="_blank"
          rel="noopener noreferrer"
          className={clsx(
            classes.downloadVisaInvitationButton,
            !visaButtonEnabled && classes.downloadButtonDisabled
          )}
        >
          <img
            src={DownloadIcon}
            alt=""
          />
          Invitation Letter for Visa
        </a>
        {!visaButtonEnabled && <UnavailableDocument invitationLetter /> }
      </div>
    </div>
  );
};

EventWallet.propTypes = {
  entities: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  attendeeTypeCustomFields: PropTypes.objectOf(
    CustomPropTypes.attendeeTypeCustomField
  ),
};

EventWallet.defaultProps = {
  attendeeTypeCustomFields: {},
};

const mapStateToProps = ({
  entities,
  entities: {
    attendeeTypeCustomFields,
  },
}) => ({ entities, attendeeTypeCustomFields });

export default connect(mapStateToProps)(EventWallet);
